import React, { useRef, useState } from 'react';
import useReglasMedia from '../../hooks/useReglasMedia';
import { comprobarArchivo, formatoMB } from '../../utils/envioMedia';

const CANALES = new Set(['whatsapp', 'instagram']);

const nuevaClave = () => (
  window.crypto && window.crypto.randomUUID
    ? window.crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`
);

// WhatsApp no admite caption en audio ni en sticker; en Instagram el caption va
// como mensaje de texto aparte, así que siempre se ofrece.
const admiteCaption = (plataforma, tipo) => (
  plataforma !== 'whatsapp' || !(tipo.startsWith('audio/') || tipo === 'image/webp')
);

const ESTADO_VACIO = { archivo: null, caption: '', voice: false, clave: null };

// Envuelve el compositor: el botón 📎 va dentro de .message-input-wrapper, a la
// izquierda de lo que recibe como children, y la vista previa va encima del wrapper
// como hermana suya. Así no hace falta tocar las reglas de .message-input-container.
function AdjuntarArchivo({ plataforma, children, ...props }) {
  if (!CANALES.has(plataforma)) {
    return <div className="message-input-wrapper">{children}</div>;
  }
  return <Adjuntador plataforma={plataforma} {...props}>{children}</Adjuntador>;
}

function Adjuntador({ plataforma, deshabilitado, enviando, onEnviar, chatKey, children }) {
  const reglas = useReglasMedia();
  const inputRef = useRef(null);
  const [estado, setEstado] = useState(ESTADO_VACIO);
  const [chatDelEstado, setChatDelEstado] = useState(chatKey);

  // Un archivo elegido en un chat no se manda a otro: handleSendMedia envía al chat abierto.
  if (chatDelEstado !== chatKey) {
    setChatDelEstado(chatKey);
    setEstado(ESTADO_VACIO);
  }

  const { archivo, caption, voice, clave } = estado;
  // Se calcula al pintar, no al elegir: si las reglas llegan después, el motivo aparece igual.
  const motivo = archivo ? comprobarArchivo(archivo, plataforma, reglas) : null;

  const alElegir = (e) => {
    const elegido = e.target.files && e.target.files[0];
    // Vaciar el input deja volver a elegir el mismo archivo tras «Quitar».
    e.target.value = '';
    if (!elegido) return;
    setEstado({ ...ESTADO_VACIO, archivo: elegido, clave: nuevaClave() });
  };

  // El backend compara la clave con el archivo, el caption y la nota de voz: reutilizarla
  // con otro caption o voice daría un 409. Cambiar cualquiera de los dos estrena clave.
  const alCambiarCaption = (e) => setEstado((s) => ({ ...s, caption: e.target.value, clave: nuevaClave() }));
  const alCambiarVoz = (e) => setEstado((s) => ({ ...s, voice: e.target.checked, clave: nuevaClave() }));

  const conCaption = archivo && admiteCaption(plataforma, archivo.type);
  const conVoz = archivo && plataforma === 'whatsapp' && archivo.type === 'audio/ogg';

  const alEnviar = async () => {
    const ok = await onEnviar(archivo, {
      caption: conCaption ? caption : '',
      voice: conVoz ? voice : false,
      idempotencyKey: clave,
    });
    if (ok) setEstado(ESTADO_VACIO);
  };

  return (
    <>
      {archivo ? (
        <div className="adjunto-preview">
          <span className="adjunto-preview-nombre" title={archivo.name}>{archivo.name}</span>
          <span className="adjunto-preview-tamano">{formatoMB(archivo.size)}</span>
          {motivo ? (
            <div className="adjunto-preview-motivo" role="alert">{motivo}</div>
          ) : (
            <>
              {conCaption ? (
                <input
                  type="text"
                  className="adjunto-preview-caption"
                  aria-label="Texto del archivo"
                  placeholder="Añade un texto (opcional)"
                  value={caption}
                  onChange={alCambiarCaption}
                />
              ) : null}
              {conVoz ? (
                <label className="adjunto-preview-voz">
                  <input type="checkbox" checked={voice} onChange={alCambiarVoz} />
                  {' '}Nota de voz
                </label>
              ) : null}
              <button
                type="button"
                className="admin-btn adjunto-preview-enviar"
                onClick={alEnviar}
                disabled={enviando || deshabilitado}
              >
                {enviando ? 'Enviando…' : 'Enviar'}
              </button>
            </>
          )}
          <button
            type="button"
            className="admin-btn secondary adjunto-preview-quitar"
            onClick={() => setEstado(ESTADO_VACIO)}
            disabled={enviando}
          >
            Quitar
          </button>
        </div>
      ) : null}
      <div className="message-input-wrapper">
        <button
          type="button"
          className="adjuntar-btn"
          aria-label="Adjuntar archivo"
          title="Adjuntar archivo"
          onClick={() => inputRef.current.click()}
          disabled={deshabilitado || enviando}
        >
          📎
        </button>
        <input ref={inputRef} type="file" hidden onChange={alElegir} />
        {children}
      </div>
    </>
  );
}

export default AdjuntarArchivo;
